import React, { useState, useEffect } from "react";
import { C, getPerms, getPrimaryRole } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, MeetingsTab, TransactionsTab,
  OperationalPlanTab, ReportsTab, useToast
} from "./erp-shared";
import { MonthlyReportButton } from "./erp-monthly-report";
import {
  getCommsNews, createCommsNews,
  getCommsPublications, createCommsPublication,
  getCommsMedia, createCommsMedia,
  getCommsSuppliers, createCommsSupplier,
  getCommsSocialAccounts, createCommsSocialAccount, updateCommsSocialAccount,
  getCommsAchievements, createCommsAchievement,
} from "./supabaseClient";
import {
  Megaphone, FileText, Plus, Search, CheckCircle, Clock,
  Calendar, Target, Eye, EyeOff, Edit, Star, Phone, Mail,
  TrendingUp, Globe, Video, Newspaper, Share2,
  MessageCircle, Heart, Users, Layers,
  ExternalLink, Pen, Printer, Send,
  Building, Award, Trophy, KeyRound, Briefcase
} from "lucide-react";

/* ✦ محوّلات صفوف DB (snake_case) → camelCase الذي تقرأه الواجهة. */
function mapNewsRow(r){return{id:r.id,title:r.title,category:r.category||"",date:r.news_date||"",author:r.author||"—",views:Number(r.views)||0,shares:Number(r.shares)||0,status:r.status||"draft",excerpt:r.excerpt||"",content:r.content||"",color:r.color||"#c026d3"};}
function mapPubRow(r){return{id:r.id,name:r.name,type:r.type||"",language:r.language||"",copies:Number(r.copies)||0,cost:Number(r.cost)||0,status:r.status||"planned"};}
function mapMediaRow(r){return{id:r.id,title:r.title,type:r.type||"",outlet:r.outlet||"",date:r.media_date||"",summary:r.summary||"",link:r.link===true};}
function mapSupplierRow(r){return{id:r.id,company:r.company,type:r.type||"",service:r.service||"",status:r.status||"active",contacts:r.contacts||[],cr:r.cr||"",iban:r.iban||"",address:r.address||"",contractValue:Number(r.contract_value)||0,startDate:r.start_date||"",endDate:r.end_date||"",rating:Number(r.rating)||0,details:r.details||"",achievements:r.achievements||"",recommendRenewal:r.recommend_renewal!==false,recommendReason:r.recommend_reason||"",notes:r.notes||""};}
function mapAccountRow(r){return{id:r.id,platform:r.platform||"",username:r.username||"",email:r.email||"",phone:r.phone||"",password:r.password||"",owner:r.owner||"—",followers:Number(r.followers)||0,status:r.status||"active",notes:r.notes||""};}
function mapAchievementRow(r){return{id:r.id,title:r.title,description:r.description||"",date:r.achievement_date||"",department:r.department||"—",impact:r.impact||"",numbers:r.numbers||""};}

/* ═════════════════════════════════════════════
   ✦ Communications Page
   ═════════════════════════════════════════════ */
export default function CommunicationsPage({ user }) {
  const [tab, setTab] = useState("news");
  const [triggers, setTriggers] = useState({ supplier: 0, account: 0 });
  const perms = getPerms(user);

  const [news, setNews] = useState([]);
  useEffect(() => { getCommsNews().then(d => setNews((d || []).map(mapNewsRow))).catch(() => {}); }, []);

  const [pubs, setPubs] = useState([]);
  useEffect(() => { getCommsPublications().then(d => setPubs((d || []).map(mapPubRow))).catch(() => {}); }, []);

  const [media, setMedia] = useState([]);
  useEffect(() => { getCommsMedia().then(d => setMedia((d || []).map(mapMediaRow))).catch(() => {}); }, []);

  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => { getCommsSuppliers().then(d => setSuppliers((d || []).map(mapSupplierRow))).catch(() => {}); }, []);

  const [accounts, setAccounts] = useState([]);
  useEffect(() => { getCommsSocialAccounts().then(d => setAccounts((d || []).map(mapAccountRow))).catch(() => {}); }, []);

  const [achievements, setAchievements] = useState([]);
  useEffect(() => { getCommsAchievements().then(d => setAchievements((d || []).map(mapAchievementRow))).catch(() => {}); }, []);

  const tabs = [
    { key: "news", label: "الأخبار والمحتوى", icon: Newspaper },
    { key: "social", label: "السوشيال ميديا", icon: Share2 },
    { key: "publications", label: "المطبوعات", icon: Printer },
    { key: "media", label: "التغطيات الإعلامية", icon: Video },
    { key: "suppliers", label: "الموردين والتسويق", icon: Briefcase },
    { key: "accounts", label: "حسابات التواصل", icon: KeyRound },
    { key: "achievements", label: "الإنجازات", icon: Trophy },
    { key: "meetings", label: "الاجتماعات", icon: Calendar },
    { key: "transactions", label: "المعاملات المالية", icon: FileText },
    { key: "plan", label: "الخطة التشغيلية", icon: Target },
    { key: "reports", label: "التقارير", icon: FileText },
  ];

  function fireQuick(targetTab, triggerKey) {
    setTab(targetTab);
    setTriggers(prev => ({ ...prev, [triggerKey]: prev[triggerKey] + 1 }));
  }

  const reportSources = [
    {
      label: "الإنجازات", mode: "monthly", dateField: "date", rows: achievements,
      columns: [
        { key: "title", label: "الإنجاز" },
        { key: "department", label: "القسم" },
        { key: "date", label: "التاريخ" },
        { key: "numbers", label: "الأرقام" },
      ],
    },
    {
      label: "الأخبار والمحتوى", mode: "monthly", dateField: "date", rows: news,
      columns: [
        { key: "title", label: "العنوان" },
        { key: "category", label: "التصنيف" },
        { key: "author", label: "الكاتب" },
        { key: "date", label: "التاريخ" },
        { key: "views", label: "المشاهدات" },
      ],
    },
    {
      label: "التغطيات الإعلامية", mode: "monthly", dateField: "date", rows: media,
      columns: [
        { key: "title", label: "العنوان" },
        { key: "type", label: "النوع" },
        { key: "outlet", label: "الجهة" },
        { key: "date", label: "التاريخ" },
      ],
    },
    { label: "الموردون والتسويق", mode: "cumulative", rows: suppliers },
    { label: "حسابات التواصل", mode: "cumulative", rows: accounts },
    { label: "المطبوعات", mode: "cumulative", rows: pubs },
  ];

  return (
    <div>
      <ModuleHeader icon={Megaphone} title="الاتصال المؤسسي" subtitle="إدارة المحتوى الإعلامي والسوشيال ميديا والمطبوعات">
        <MonthlyReportButton moduleTitle="الاتصال المؤسسي" sources={reportSources} budget={200000} />
      </ModuleHeader>

      {perms.canEdit && (
        <div style={cq.row}>
          <button style={cq.btn("#c026d3")} onClick={() => fireQuick("suppliers", "supplier")}>
            <Plus size={16} /><span>اتفاق مورد جديد</span>
          </button>
          <button style={cq.btn("#0891b2")} onClick={() => fireQuick("accounts", "account")}>
            <Plus size={16} /><span>إضافة حساب تواصل</span>
          </button>
        </div>
      )}

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "news" && <NewsTab canEdit={perms.canEdit} news={news} setNews={setNews} />}
      {tab === "social" && <SocialTab />}
      {tab === "publications" && <PublicationsTab canEdit={perms.canEdit} pubs={pubs} setPubs={setPubs} />}
      {tab === "media" && <MediaTab canEdit={perms.canEdit} coverage={media} setCoverage={setMedia} />}
      {tab === "suppliers" && <SuppliersTab canEdit={perms.canEdit} triggerOpen={triggers.supplier} suppliers={suppliers} setSuppliers={setSuppliers} />}
      {tab === "accounts" && <SocialAccountsTab canEdit={perms.canEdit} userRole={getPrimaryRole(user)} triggerOpen={triggers.account} accounts={accounts} setAccounts={setAccounts} />}
      {tab === "achievements" && <AchievementsTab canEdit={perms.canEdit} achievements={achievements} setAchievements={setAchievements} />}
      {tab === "meetings" && <MeetingsTab departmentName="الاتصال المؤسسي" module="comms" />}
      {tab === "transactions" && <TransactionsTab budget={200000} module="comms" />}
      {tab === "plan" && <OperationalPlanTab departmentKey="communications" />}
      {tab === "reports" && <ReportsTab departmentName="الاتصال المؤسسي" />}
    </div>
  );
}

const cq = {
  row: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18 },
  btn: (color) => ({
    display: "flex", alignItems: "center", gap: 8,
    padding: "10px 16px", borderRadius: 10,
    background: `${color}10`, color, border: `1px solid ${color}30`,
    fontFamily: "'Readex Pro', sans-serif", fontSize: 13, fontWeight: 700,
    cursor: "pointer", transition: "all 0.15s",
  }),
};

/* ─────────────────────────────────────────────
   ✦ News Tab — الأخبار والمحتوى
   ───────────────────────────────────────────── */
const INITIAL_NEWS = { title: "", category: "", statusLabel: "مسودة", excerpt: "", content: "", author: "" };

function NewsTab({ canEdit, news, setNews }) {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_NEWS);
  const [showToast, ToastView] = useToast();

  const statusLabelToKey = { "مسودة": "draft", "مراجعة": "review", "منشور": "published" };

  async function handleSave() {
    if (!form.title || !form.content) {
      showToast("العنوان والمحتوى مطلوبان", "error");
      return;
    }
    const payload = {
      title: form.title,
      category: form.category || "أخبار الجمعية",
      news_date: new Date().toISOString().slice(0, 10),
      author: form.author || "—",
      views: 0,
      shares: 0,
      status: statusLabelToKey[form.statusLabel] || "draft",
      excerpt: form.excerpt || "",
      content: form.content,
      color: "#c026d3",
    };
    try {
      const created = await createCommsNews(payload);
      setNews(prev => [mapNewsRow(created), ...prev]);
      setForm(INITIAL_NEWS);
      setShowAdd(false);
      showToast("تم حفظ الخبر");
    } catch (e) {
      showToast(e.message || "تعذّر الحفظ", "error");
    }
  }

  const NEWS_STATUSES = [
    { key: "published", label: "منشور", color: C.success },
    { key: "draft", label: "مسودة", color: C.warning },
    { key: "review", label: "مراجعة", color: "#2563eb" },
    { key: "archived", label: "مؤرشف", color: C.textLight },
  ];

  const filtered = news.filter(n => {
    const matchSearch = n.title.includes(search);
    const matchStatus = filterStatus === "all" || n.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الأخبار" value={news.length} icon={Newspaper} color="#c026d3" />
        <KPICard title="منشورة" value={news.filter(n => n.status === "published").length} icon={CheckCircle} color={C.success} />
        <KPICard title="مسودات" value={news.filter(n => n.status === "draft").length} icon={Pen} color={C.warning} />
        <KPICard title="إجمالي المشاهدات" value={news.reduce((s, n) => s + n.views, 0).toLocaleString()} icon={Eye} color={C.teal} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالعنوان..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الحالات</option>
              {NEWS_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">خبر جديد</Btn>}
          </div>
        </div>
      </Card>

      <div style={styles.newsGrid}>
        {filtered.map(n => (
          <Card key={n.id} style={styles.newsCard} onClick={() => setSelected(n)}>
            <div style={{ ...styles.newsStrip, background: n.color }} />
            <div style={{ padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: `${n.color}12`, color: n.color }}>{n.category}</span>
                <StatusBadge status={n.status} statuses={NEWS_STATUSES} />
              </div>
              <h4 style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 700, color: C.primary, lineHeight: 1.5 }}>{n.title}</h4>
              <p style={{ margin: 0, fontSize: 12, color: C.textLight, lineHeight: 1.6 }}>{n.excerpt}</p>
              <div style={styles.newsMeta}>
                <span><Calendar size={12} /> {n.date}</span>
                <span><Eye size={12} /> {n.views}</span>
                <span><Share2 size={12} /> {n.shares}</span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {selected && (
        <Modal title={selected.title} onClose={() => setSelected(null)} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={styles.detailRow}><strong>الفئة:</strong> {selected.category}</div>
            <div style={styles.detailRow}><strong>التاريخ:</strong> {selected.date}</div>
            <div style={styles.detailRow}><strong>الكاتب:</strong> {selected.author}</div>
            <div style={styles.detailRow}><strong>المشاهدات:</strong> {selected.views}</div>
            <div style={styles.detailRow}><strong>المشاركات:</strong> {selected.shares}</div>
            <div style={styles.detailRow}><strong>المحتوى:</strong><p style={{ margin: "6px 0 0", lineHeight: 1.8 }}>{selected.content}</p></div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إنشاء خبر جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_NEWS); }} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="العنوان" required placeholder="عنوان الخبر" value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الفئة" type="select" options={["أخبار الجمعية","فعاليات","إنجازات","مجتمع","تقارير","إعلانات"]} required value={form.category} onChange={v => setForm({ ...form, category: v })} />
              <Field label="الحالة" type="select" options={["مسودة","مراجعة","منشور"]} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
            </div>
            <Field label="المختصر" type="textarea" placeholder="ملخص الخبر..." rows={2} value={form.excerpt} onChange={v => setForm({ ...form, excerpt: v })} />
            <Field label="المحتوى الكامل" type="textarea" required placeholder="نص الخبر..." rows={5} value={form.content} onChange={v => setForm({ ...form, content: v })} />
            <Field label="الكاتب" placeholder="اسم الكاتب" value={form.author} onChange={v => setForm({ ...form, author: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Send} onClick={handleSave}>نشر</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_NEWS); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Social Media Tab
   ───────────────────────────────────────────── */
function SocialTab() {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي المتابعين" value="0" icon={Users} color="#c026d3" />
        <KPICard title="المنشورات الشهرية" value="0" icon={Share2} color={C.teal} />
        <KPICard title="التفاعل" value="0" icon={Heart} color="#db2777" />
        <KPICard title="الوصول" value="0" icon={Eye} color="#2563eb" />
      </div>

      {/* Platform cards */}
      <div style={styles.platformsGrid}>
        {SOCIAL_PLATFORMS.map((p, i) => (
          <Card key={i} style={styles.platformCard}>
            <div style={styles.platformHeader}>
              <div style={{ ...styles.platformIcon, background: `${p.color}15` }}>
                <Globe size={22} color={p.color} />
              </div>
              <div>
                <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.primary }}>{p.name}</h4>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: C.textLight }}>@{p.handle}</p>
              </div>
            </div>
            <div style={styles.platformStats}>
              <div style={styles.platformStat}>
                <span style={styles.platformStatValue}>{p.followers}</span>
                <span style={styles.platformStatLabel}>متابع</span>
              </div>
              <div style={styles.platformStat}>
                <span style={styles.platformStatValue}>{p.posts}</span>
                <span style={styles.platformStatLabel}>منشور</span>
              </div>
              <div style={styles.platformStat}>
                <span style={styles.platformStatValue}>{p.engagement}</span>
                <span style={styles.platformStatLabel}>تفاعل</span>
              </div>
            </div>
            <div style={styles.platformGrowth}>
              <TrendingUp size={14} color={C.success} />
              <span style={{ fontSize: 13, fontWeight: 600, color: C.success }}>{p.growth}</span>
              <span style={{ fontSize: 12, color: C.textLight }}>نمو هذا الشهر</span>
            </div>
          </Card>
        ))}
      </div>

      {/* Recent posts */}
      <Card style={{ padding: 20, marginTop: 16 }}>
        <h4 style={styles.sectionTitle}><MessageCircle size={18} color="#c026d3" /> آخر المنشورات</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {RECENT_POSTS.map((p, i) => (
            <div key={i} style={styles.postItem}>
              <div style={{ ...styles.postPlatform, background: `${p.color}15`, color: p.color }}>{p.platform}</div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.primary }}>{p.text}</p>
                <div style={{ display: "flex", gap: 12, marginTop: 4, fontSize: 11, color: C.textLight }}>
                  <span>{p.date}</span>
                  <span><Heart size={11} /> {p.likes}</span>
                  <span><Share2 size={11} /> {p.shares}</span>
                  <span><MessageCircle size={11} /> {p.comments}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Publications Tab — المطبوعات
   ───────────────────────────────────────────── */
const INITIAL_PUB = { name: "", type: "", language: "", copies: "", cost: "" };

function PublicationsTab({ canEdit, pubs, setPubs }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_PUB);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.name || !form.type || !form.language) {
      showToast("الاسم والنوع واللغة مطلوبة", "error");
      return;
    }
    const payload = {
      name: form.name,
      type: form.type,
      language: form.language,
      copies: Number(form.copies) || 0,
      cost: Number(form.cost) || 0,
      status: "planned",
    };
    try {
      const created = await createCommsPublication(payload);
      setPubs(prev => [...prev, mapPubRow(created)]);
      setForm(INITIAL_PUB);
      setShowAdd(false);
      showToast("تمت إضافة المطبوعة");
    } catch (e) {
      showToast(e.message || "تعذّر الحفظ", "error");
    }
  }

  const PUB_STATUSES = [
    { key: "printed", label: "مطبوع", color: C.success },
    { key: "design", label: "تصميم", color: "#2563eb" },
    { key: "review", label: "مراجعة", color: C.warning },
    { key: "planned", label: "مخطط", color: C.textLight },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="المطبوعات" value={pubs.length} icon={Printer} color="#c026d3" />
        <KPICard title="مطبوعة" value={pubs.filter(p => p.status === "printed").length} icon={CheckCircle} color={C.success} />
        <KPICard title="إجمالي النسخ" value={pubs.reduce((s, p) => s + p.copies, 0).toLocaleString()} icon={Layers} color={C.teal} />
      </div>

      {canEdit && (
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">مطبوعة جديدة</Btn>
        </div>
      )}

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "name", label: "اسم المطبوعة", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "type", label: "النوع", width: "100px" },
            { key: "language", label: "اللغة", width: "90px" },
            { key: "copies", label: "النسخ", width: "80px", render: v => v.toLocaleString() },
            { key: "cost", label: "التكلفة", width: "100px", render: v => `${v.toLocaleString()} ر` },
            { key: "status", label: "الحالة", width: "90px", render: v => <StatusBadge status={v} statuses={PUB_STATUSES} /> },
          ]}
          data={pubs}
        />
      </Card>

      {showAdd && (
        <Modal title="إضافة مطبوعة" onClose={() => { setShowAdd(false); setForm(INITIAL_PUB); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم المطبوعة" required placeholder="مثال: كتيّب أحكام الصيام" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={["كتيّب","بروشور","لوحة","بانر","ملصق","مجلة","تقرير سنوي"]} required value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="اللغة" type="select" options={["العربية","الإنجليزية","الأردية","الهندية","الفلبينية","متعدد"]} required value={form.language} onChange={v => setForm({ ...form, language: v })} />
              <Field label="عدد النسخ" type="number" placeholder="0" value={form.copies} onChange={v => setForm({ ...form, copies: v })} />
            </div>
            <Field label="التكلفة (ريال)" type="number" placeholder="0" value={form.cost} onChange={v => setForm({ ...form, cost: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Printer} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_PUB); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Media Tab — التغطيات الإعلامية
   ───────────────────────────────────────────── */
const INITIAL_MEDIA = { title: "", type: "", outlet: "", date: "", summary: "", link: "" };

function MediaTab({ canEdit, coverage, setCoverage }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_MEDIA);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.title || !form.type || !form.outlet || !form.date) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const payload = {
      title: form.title,
      type: form.type,
      outlet: form.outlet,
      media_date: form.date,
      summary: form.summary || "",
      link: !!form.link,
    };
    try {
      const created = await createCommsMedia(payload);
      setCoverage(prev => [mapMediaRow(created), ...prev]);
      setForm(INITIAL_MEDIA);
      setShowAdd(false);
      showToast("تم حفظ التغطية");
    } catch (e) {
      showToast(e.message || "تعذّر الحفظ", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="التغطيات" value={coverage.length} icon={Video} color="#ea580c" />
        <KPICard title="صحفية" value={coverage.filter(c => c.type === "صحفي").length} icon={Newspaper} color={C.primary} />
        <KPICard title="مرئية" value={coverage.filter(c => c.type === "مرئي").length} icon={Video} color="#c026d3" />
      </div>

      {canEdit && (
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة تغطية</Btn>
        </div>
      )}

      <div style={styles.mediaGrid}>
        {coverage.map(c => (
          <Card key={c.id} style={styles.mediaCard}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: c.type === "صحفي" ? `${C.primary}12` : c.type === "مرئي" ? `#c026d312` : `${C.teal}12`, color: c.type === "صحفي" ? C.primary : c.type === "مرئي" ? "#c026d3" : C.teal }}>{c.type}</span>
              <span style={{ fontSize: 11, color: C.textLight }}>{c.date}</span>
            </div>
            <h4 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, color: C.primary }}>{c.title}</h4>
            <p style={{ margin: "0 0 8px", fontSize: 13, color: C.textLight }}>{c.outlet}</p>
            <p style={{ margin: 0, fontSize: 12, color: C.primary, lineHeight: 1.6 }}>{c.summary}</p>
            {c.link && (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <a href="#" style={styles.mediaLink}><ExternalLink size={12} /> عرض التغطية</a>
            )}
          </Card>
        ))}
      </div>

      {showAdd && (
        <Modal title="إضافة تغطية إعلامية" onClose={() => { setShowAdd(false); setForm(INITIAL_MEDIA); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="العنوان" required placeholder="عنوان التغطية" value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={["صحفي","مرئي","إذاعي","إلكتروني"]} required value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="الجهة الإعلامية" required placeholder="مثال: صحيفة الجزيرة" value={form.outlet} onChange={v => setForm({ ...form, outlet: v })} />
            </div>
            <Field label="التاريخ" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
            <Field label="ملخص" type="textarea" placeholder="ملخص التغطية..." value={form.summary} onChange={v => setForm({ ...form, summary: v })} />
            <Field label="الرابط" placeholder="https://..." value={form.link} onChange={v => setForm({ ...form, link: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Video} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_MEDIA); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Suppliers Tab — الموردين والتسويق
   ───────────────────────────────────────────── */
const SUPPLIER_TYPES = ["شركة", "مؤسسة", "فرد"];
const SUPPLIER_SERVICES = ["إدارة حسابات تواصل", "حملة إعلانية", "تصميم", "تصوير", "إنتاج فيديو", "تطوير موقع", "طباعة", "ترجمة", "علاقات عامة", "استشارات", "أخرى"];
const CONTRACT_STATUSES = [
  { key: "active",    label: "نشط", color: C.success },
  { key: "expired",   label: "منتهي", color: C.danger },
  { key: "renewing",  label: "قيد التجديد", color: C.warning },
  { key: "cancelled", label: "ملغي", color: C.textLight },
];
const CONTRACT_STATUS_OPTIONS = ["نشط", "منتهي", "قيد التجديد", "ملغي"];
const CONTRACT_LABEL_TO_KEY = { "نشط": "active", "منتهي": "expired", "قيد التجديد": "renewing", "ملغي": "cancelled" };

const INITIAL_SUPPLIER = {
  company: "", type: "شركة", service: "إدارة حسابات تواصل", statusLabel: "نشط",
  contact1Name: "", contact1Phone: "", contact1Email: "",
  contact2Name: "", contact2Phone: "", contact2Email: "",
  contact3Name: "", contact3Phone: "", contact3Email: "",
  cr: "", iban: "", address: "",
  contractValue: "", startDate: "", endDate: "",
  rating: 5, details: "", achievements: "",
  recommendRenewal: "نعم", recommendReason: "",
  notes: "",
};

function RatingStars({ value, onChange }) {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {stars.map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange && onChange(n)}
          style={{ background: "none", border: "none", cursor: onChange ? "pointer" : "default", padding: 2 }}
        >
          <Star size={18} fill={n <= value ? C.accent : "none"} color={n <= value ? C.accent : C.textLight} />
        </button>
      ))}
    </div>
  );
}

function SuppliersTab({ canEdit, triggerOpen = 0, suppliers, setSuppliers }) {
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_SUPPLIER);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const active = suppliers.filter(s => s.status === "active");
  const renewing = suppliers.filter(s => s.status === "renewing");
  const expired = suppliers.filter(s => s.status === "expired");
  const activeValue = active.reduce((s, x) => s + (Number(x.contractValue) || 0), 0);

  async function handleSave() {
    if (!form.company || !form.contact1Name) {
      showToast("اسم الشركة والمسؤول الأول مطلوبان", "error");
      return;
    }
    const payload = {
      company: form.company,
      type: form.type,
      service: form.service,
      status: CONTRACT_LABEL_TO_KEY[form.statusLabel] || "active",
      contacts: [
        { name: form.contact1Name, phone: form.contact1Phone, email: form.contact1Email },
        form.contact2Name ? { name: form.contact2Name, phone: form.contact2Phone, email: form.contact2Email } : null,
        form.contact3Name ? { name: form.contact3Name, phone: form.contact3Phone, email: form.contact3Email } : null,
      ].filter(Boolean),
      cr: form.cr,
      iban: form.iban,
      address: form.address,
      contract_value: Number(form.contractValue) || 0,
      start_date: form.startDate,
      end_date: form.endDate,
      rating: Number(form.rating) || 0,
      details: form.details,
      achievements: form.achievements,
      recommend_renewal: form.recommendRenewal === "نعم",
      recommend_reason: form.recommendReason,
      notes: form.notes,
    };
    try {
      const created = await createCommsSupplier(payload);
      setSuppliers(prev => [mapSupplierRow(created), ...prev]);
      setForm(INITIAL_SUPPLIER);
      setShowAdd(false);
      showToast("تمت إضافة المورّد");
    } catch (e) {
      showToast(e.message || "تعذّر الحفظ", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}

      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الموردين" value={suppliers.length} icon={Briefcase} color="#c026d3" />
        <KPICard title="عقود نشطة" value={active.length} icon={CheckCircle} color={C.success} />
        <KPICard title="قيمة العقود النشطة" value={`${(activeValue / 1000).toFixed(0)}K`} icon={TrendingUp} color={C.accent} subtitle="ر.س" />
        <KPICard title="منتهية / قيد التجديد" value={expired.length + renewing.length} icon={Clock} color={C.warning} />
      </div>

      {canEdit && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة مورد جديد</Btn>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 16 }}>
        {suppliers.map(s => (
          <Card key={s.id} style={{ padding: 18 }} onClick={() => setSelected(s)}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: "#c026d315", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Building size={22} color="#c026d3" />
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.primary }}>{s.company}</h4>
                  <p style={{ margin: "2px 0 0", fontSize: 11, color: C.textLight }}>{s.service}</p>
                </div>
              </div>
              <StatusBadge status={s.status} statuses={CONTRACT_STATUSES} />
            </div>

            {s.details && (
              <p style={{ margin: "8px 0", fontSize: 12, color: C.textLight, lineHeight: 1.6 }}>
                <strong style={{ color: C.primary }}>التفاصيل:</strong> {s.details}
              </p>
            )}

            {s.achievements && (
              <p style={{ margin: "8px 0", padding: 8, background: `${C.success}08`, borderRadius: 6, fontSize: 12, color: C.primary, lineHeight: 1.6 }}>
                <Trophy size={12} color={C.success} /> <strong>الإنجازات:</strong> {s.achievements}
              </p>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: C.accent }}>
                {(s.contractValue || 0).toLocaleString()} ر.س
              </span>
              <RatingStars value={s.rating} />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 10, fontSize: 11, color: C.textLight }}>
              {s.contacts && s.contacts[0] && (
                <>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Users size={12} /> {s.contacts[0].name} {s.contacts.length > 1 ? `(+${s.contacts.length - 1})` : ""}</span>
                  {s.contacts[0].phone && <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Phone size={12} /> <span dir="ltr">{s.contacts[0].phone}</span></span>}
                  {s.contacts[0].email && <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Mail size={12} /> <span dir="ltr">{s.contacts[0].email}</span></span>}
                </>
              )}
              {s.type && <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: `${C.primary}10`, color: C.primary, width: "fit-content" }}>{s.type}</span>}
              {s.startDate && s.endDate && (
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Calendar size={12} /> {s.startDate} → {s.endDate}</span>
              )}
            </div>
          </Card>
        ))}
      </div>

      {selected && (
        <Modal title={selected.company} onClose={() => setSelected(null)} width={620}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={styles.detailRow}><strong>نوع الخدمة:</strong> {selected.service}</div>
            <div style={styles.detailRow}><strong>حالة التعاقد:</strong> <StatusBadge status={selected.status} statuses={CONTRACT_STATUSES} /></div>
            {selected.type && <div style={styles.detailRow}><strong>النوع:</strong> {selected.type}</div>}
            {selected.contacts && selected.contacts.map((c, i) => (
              <div key={i} style={{ padding: 10, background: C.bg, borderRadius: 8 }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: C.teal }}>المسؤول {i + 1}:</p>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: C.primary }}>
                  <strong>{c.name}</strong>
                  {c.phone && <span style={{ marginRight: 12, display: "inline-flex", alignItems: "center", gap: 4 }} dir="ltr"><Phone size={12} /> {c.phone}</span>}
                  {c.email && <span style={{ marginRight: 12, display: "inline-flex", alignItems: "center", gap: 4 }} dir="ltr"><Mail size={12} /> {c.email}</span>}
                </p>
              </div>
            ))}
            {selected.cr && <div style={styles.detailRow}><strong>السجل التجاري / رقم الهوية:</strong> <span dir="ltr">{selected.cr}</span></div>}
            {selected.iban && <div style={styles.detailRow}><strong>IBAN / رقم الحساب:</strong> <span dir="ltr">{selected.iban}</span></div>}
            {selected.address && <div style={styles.detailRow}><strong>العنوان:</strong> {selected.address}</div>}
            <div style={styles.detailRow}><strong>قيمة العقد:</strong> {(selected.contractValue || 0).toLocaleString()} ر.س</div>
            <div style={styles.detailRow}><strong>الفترة:</strong> {selected.startDate || "—"} → {selected.endDate || "—"}</div>
            <div style={styles.detailRow}><strong>التقييم:</strong> <RatingStars value={selected.rating} /></div>
            {selected.details && <div style={styles.detailRow}><strong>تفاصيل التعاقد:</strong><p style={{ margin: "4px 0 0", color: C.primary, lineHeight: 1.7 }}>{selected.details}</p></div>}
            {selected.achievements && <div style={styles.detailRow}><strong>الإنجازات:</strong><p style={{ margin: "4px 0 0", color: C.primary, lineHeight: 1.7 }}>{selected.achievements}</p></div>}
            {selected.notes && <div style={styles.detailRow}><strong>ملاحظات:</strong> {selected.notes}</div>}
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إضافة مورّد / شركة جديدة" onClose={() => { setShowAdd(false); setForm(INITIAL_SUPPLIER); }} width={760}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Basic */}
            <Field label="اسم الشركة / الفرد" required placeholder="مثال: استوديو الإبداع" value={form.company} onChange={v => setForm({ ...form, company: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={SUPPLIER_TYPES} value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="نوع الخدمة المقدمة" type="select" options={SUPPLIER_SERVICES} value={form.service} onChange={v => setForm({ ...form, service: v })} />
              <Field label="نوع التعاقد" type="select" options={CONTRACT_STATUS_OPTIONS} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
            </div>

            {/* Contact 1 — required */}
            <div style={{ padding: 14, background: `${C.teal}06`, borderRadius: 10, border: `1px solid ${C.teal}20` }}>
              <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: C.teal }}>المسؤول الأول (مطلوب)</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label="الاسم" required placeholder="اسم المسؤول" value={form.contact1Name} onChange={v => setForm({ ...form, contact1Name: v })} />
                <Field label="الهاتف" placeholder="05xxxxxxxx" value={form.contact1Phone} onChange={v => setForm({ ...form, contact1Phone: v })} />
                <Field label="الإيميل" placeholder="email@company.com" value={form.contact1Email} onChange={v => setForm({ ...form, contact1Email: v })} />
              </div>
            </div>

            {/* Contact 2 */}
            <div style={{ padding: 14, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
              <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: C.textLight }}>المسؤول الثاني (اختياري)</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label="الاسم" placeholder="اسم المسؤول" value={form.contact2Name} onChange={v => setForm({ ...form, contact2Name: v })} />
                <Field label="الهاتف" placeholder="05xxxxxxxx" value={form.contact2Phone} onChange={v => setForm({ ...form, contact2Phone: v })} />
                <Field label="الإيميل" placeholder="email@company.com" value={form.contact2Email} onChange={v => setForm({ ...form, contact2Email: v })} />
              </div>
            </div>

            {/* Contact 3 */}
            <div style={{ padding: 14, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
              <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: C.textLight }}>المسؤول الثالث (اختياري)</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label="الاسم" placeholder="اسم المسؤول" value={form.contact3Name} onChange={v => setForm({ ...form, contact3Name: v })} />
                <Field label="الهاتف" placeholder="05xxxxxxxx" value={form.contact3Phone} onChange={v => setForm({ ...form, contact3Phone: v })} />
                <Field label="الإيميل" placeholder="email@company.com" value={form.contact3Email} onChange={v => setForm({ ...form, contact3Email: v })} />
              </div>
            </div>

            {/* Legal/Financial */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="السجل التجاري / رقم الهوية" placeholder="1010xxxxxx" value={form.cr} onChange={v => setForm({ ...form, cr: v })} />
              <Field label="IBAN / رقم الحساب" placeholder="SA00 0000 ..." value={form.iban} onChange={v => setForm({ ...form, iban: v })} />
            </div>
            <Field label="العنوان" placeholder="الرياض - الحي" value={form.address} onChange={v => setForm({ ...form, address: v })} />

            {/* Contract */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="قيمة التعاقد (ر.س)" type="number" placeholder="0" value={form.contractValue} onChange={v => setForm({ ...form, contractValue: v })} />
              <Field label="بداية العقد" type="date" value={form.startDate} onChange={v => setForm({ ...form, startDate: v })} />
              <Field label="نهاية العقد" type="date" value={form.endDate} onChange={v => setForm({ ...form, endDate: v })} />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.textLight }}>
                تقييم الأداء (1-5)
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 10, background: C.bg, borderRadius: 8 }}>
                <RatingStars value={form.rating} onChange={v => setForm({ ...form, rating: v })} />
                <span style={{ fontSize: 13, color: C.primary, fontWeight: 600 }}>{form.rating}/5</span>
              </div>
            </div>

            <Field label="تفاصيل التعاقد (الخدمات المتفق عليها)" type="textarea" placeholder="وصف الخدمات..." rows={3} value={form.details} onChange={v => setForm({ ...form, details: v })} />
            <Field label="الإنجازات والنتائج" type="textarea" placeholder="ما حققوه..." rows={3} value={form.achievements} onChange={v => setForm({ ...form, achievements: v })} />

            <div style={{ padding: 12, background: `${C.success}06`, borderRadius: 10, border: `1px solid ${C.success}30` }}>
              <Field label="هل يُوصى بالتعاون مجدداً؟" type="select" options={["نعم", "لا"]} value={form.recommendRenewal} onChange={v => setForm({ ...form, recommendRenewal: v })} />
              <Field label="سبب التوصية" type="textarea" rows={2} placeholder="اشرح سبب التوصية بالتعاون مجدداً أو عدم التعاون..." value={form.recommendReason} onChange={v => setForm({ ...form, recommendReason: v })} />
            </div>

            <Field label="ملاحظات" type="textarea" placeholder="ملاحظات إضافية..." rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Briefcase} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_SUPPLIER); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Social Accounts Tab — حسابات التواصل
   ───────────────────────────────────────────── */
const SOCIAL_PLATFORM_OPTIONS = ["تويتر", "إنستجرام", "يوتيوب", "فيسبوك", "واتساب", "تيك توك", "سناب شات", "تلجرام"];
const ACCOUNT_STATUSES = [
  { key: "active", label: "نشط", color: C.success },
  { key: "paused", label: "معلّق", color: C.textLight },
];
const ACCOUNT_STATUS_OPTIONS = ["نشط", "معلّق"];
const ACCOUNT_LABEL_TO_KEY = { "نشط": "active", "معلّق": "paused" };

const INITIAL_ACCOUNT = {
  platform: "تويتر (X)", username: "", email: "", phone: "",
  password: "", owner: "", followers: "", statusLabel: "نشط", notes: "",
};

function SocialAccountsTab({ canEdit, userRole, triggerOpen = 0, accounts, setAccounts }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [revealed, setRevealed] = useState({}); // id -> bool
  const [form, setForm] = useState(INITIAL_ACCOUNT);
  const [showToast, ToastView] = useToast();

  const canSeePassword = userRole === "exec" || userRole === "system";

  function openAdd() {
    setForm(INITIAL_ACCOUNT);
    setEditing(null);
    setShowAdd(true);
  }

  useEffect(() => { if (triggerOpen > 0) openAdd(); /* eslint-disable-next-line */ }, [triggerOpen]);

  function openEdit(acc) {
    setForm({
      platform: acc.platform, username: acc.username, email: acc.email,
      phone: acc.phone, password: acc.password, owner: acc.owner,
      followers: String(acc.followers || ""),
      statusLabel: acc.status === "active" ? "نشط" : "معلّق",
      notes: acc.notes,
    });
    setEditing(acc.id);
    setShowAdd(true);
  }

  async function handleSave() {
    if (!form.platform || !form.username) {
      showToast("المنصة واليوزرنيم مطلوبان", "error");
      return;
    }
    const data = {
      platform: form.platform,
      username: form.username,
      email: form.email,
      phone: form.phone,
      password: form.password,
      owner: form.owner || "—",
      followers: Number(form.followers) || 0,
      status: ACCOUNT_LABEL_TO_KEY[form.statusLabel] || "active",
      notes: form.notes,
    };
    try {
      if (editing) {
        const updated = await updateCommsSocialAccount(editing, data);
        setAccounts(prev => prev.map(a => a.id === editing ? mapAccountRow(updated) : a));
        showToast("تم تحديث الحساب");
      } else {
        const created = await createCommsSocialAccount(data);
        setAccounts(prev => [...prev, mapAccountRow(created)]);
        showToast("تمت إضافة الحساب");
      }
      setForm(INITIAL_ACCOUNT);
      setEditing(null);
      setShowAdd(false);
    } catch (e) {
      showToast(e.message || "تعذّر الحفظ", "error");
    }
  }

  function maskPwd(p) {
    if (!p) return "—";
    return "•".repeat(Math.min(p.length, 10));
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}

      <div style={styles.kpiRow}>
        <KPICard title="عدد الحسابات" value={accounts.length} icon={KeyRound} color="#c026d3" />
        <KPICard title="حسابات نشطة" value={accounts.filter(a => a.status === "active").length} icon={CheckCircle} color={C.success} />
        <KPICard title="إجمالي المتابعين" value={accounts.reduce((s, a) => s + (a.followers || 0), 0).toLocaleString()} icon={Users} color={C.teal} />
      </div>

      {!canSeePassword && (
        <div style={{ padding: 12, marginBottom: 16, background: `${C.warning}10`, borderRadius: 8, fontSize: 13, color: C.warning, display: "flex", alignItems: "center", gap: 8 }}>
          <KeyRound size={16} />
          كلمات المرور مرئية فقط للمدير التنفيذي ومدير النظام.
        </div>
      )}

      {canEdit && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <Btn icon={Plus} onClick={openAdd} size="sm">إضافة حساب</Btn>
        </div>
      )}

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "platform", label: "المنصة", width: "110px", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "username", label: "اليوزرنيم", render: v => <span style={{ fontFamily: "monospace" }} dir="ltr">@{v}</span> },
            { key: "email", label: "البريد", width: "180px", render: v => <span dir="ltr" style={{ fontSize: 12 }}>{v || "—"}</span> },
            { key: "phone", label: "الجوال", width: "120px", render: v => <span dir="ltr" style={{ fontSize: 12 }}>{v || "—"}</span> },
            {
              key: "password", label: "كلمة المرور", width: "150px", render: (v, row) => {
                const isShown = canSeePassword && revealed[row.id];
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontFamily: "monospace", color: isShown ? C.primary : C.textLight }}>
                      {isShown ? v : maskPwd(v)}
                    </span>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        if (canSeePassword) {
                          setRevealed(prev => ({ ...prev, [row.id]: !prev[row.id] }));
                        } else {
                          showToast("غير مصرح — كلمات المرور للمدير التنفيذي ومدير النظام فقط", "error");
                        }
                      }}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", alignItems: "center" }}
                      title={isShown ? "إخفاء" : "إظهار"}
                    >
                      {isShown ? <EyeOff size={14} color={C.textLight} /> : <Eye size={14} color={canSeePassword ? C.teal : C.textLight} />}
                    </button>
                  </div>
                );
              }
            },
            { key: "owner", label: "المسؤول", width: "140px" },
            { key: "followers", label: "متابعين", width: "90px", render: v => (v || 0).toLocaleString() },
            { key: "status", label: "الحالة", width: "80px", render: v => <StatusBadge status={v} statuses={ACCOUNT_STATUSES} /> },
            {
              key: "actions", label: "إجراء", width: "70px", render: (v, row) => canEdit ? (
                <button onClick={e => { e.stopPropagation(); openEdit(row); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 6 }} title="تعديل">
                  <Edit size={14} color={C.teal} />
                </button>
              ) : null
            },
          ]}
          data={accounts}
        />
      </Card>

      {showAdd && (
        <Modal title={editing ? "تعديل حساب" : "إضافة حساب جديد"} onClose={() => { setShowAdd(false); setForm(INITIAL_ACCOUNT); setEditing(null); }} width={620}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المنصة" type="select" options={SOCIAL_PLATFORM_OPTIONS} required value={form.platform} onChange={v => setForm({ ...form, platform: v })} />
              <Field label="اليوزرنيم" required placeholder="bnr_org" value={form.username} onChange={v => setForm({ ...form, username: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="البريد الإلكتروني المربوط" placeholder="account@bnr.org.sa" value={form.email} onChange={v => setForm({ ...form, email: v })} />
              <Field label="الجوال المربوط" placeholder="05xxxxxxxx" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
            </div>
            <Field label="كلمة المرور" placeholder="••••••••" value={form.password} onChange={v => setForm({ ...form, password: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="المسؤول" placeholder="اسم المسؤول" value={form.owner} onChange={v => setForm({ ...form, owner: v })} />
              <Field label="عدد المتابعين" type="number" placeholder="0" value={form.followers} onChange={v => setForm({ ...form, followers: v })} />
              <Field label="الحالة" type="select" options={ACCOUNT_STATUS_OPTIONS} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" placeholder="ملاحظات إضافية..." rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={KeyRound} onClick={handleSave}>{editing ? "حفظ التغييرات" : "إضافة"}</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_ACCOUNT); setEditing(null); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Achievements Tab — الإنجازات
   ───────────────────────────────────────────── */
const INITIAL_ACHIEVEMENT = { title: "", description: "", date: "", department: "", impact: "", numbers: "" };
const DEPARTMENT_OPTIONS = ["الجاليات", "المساجد", "النسائي", "المالية", "تنمية الموارد", "الاتصال", "التطوع", "الموارد البشرية", "الحوكمة", "مجلس الإدارة"];

function AchievementsTab({ canEdit, achievements, setAchievements }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_ACHIEVEMENT);
  const [showToast, ToastView] = useToast();

  const thisYear = new Date().getFullYear();
  const thisYearCount = achievements.filter(a => (a.date || "").startsWith(String(thisYear))).length;

  async function handleSave() {
    if (!form.title || !form.date) {
      showToast("العنوان والتاريخ مطلوبان", "error");
      return;
    }
    const payload = {
      title: form.title,
      description: form.description,
      achievement_date: form.date,
      department: form.department || "—",
      impact: form.impact,
      numbers: form.numbers,
    };
    try {
      const created = await createCommsAchievement(payload);
      setAchievements(prev => [mapAchievementRow(created), ...prev]);
      setForm(INITIAL_ACHIEVEMENT);
      setShowAdd(false);
      showToast("تمت إضافة الإنجاز");
    } catch (e) {
      showToast(e.message || "تعذّر الحفظ", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}

      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الإنجازات" value={achievements.length} icon={Trophy} color={C.accent} />
        <KPICard title="هذا العام" value={thisYearCount} icon={Award} color={C.success} />
      </div>

      {canEdit && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة إنجاز</Btn>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
        {achievements.map(a => (
          <Card key={a.id} style={{ padding: 18, borderTop: `3px solid ${C.accent}` }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: `${C.accent}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Trophy size={22} color={C.accent} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.primary }}>{a.title}</h4>
                <p style={{ margin: "2px 0 0", fontSize: 11, color: C.textLight, display: "flex", gap: 10 }}>
                  <span><Calendar size={11} /> {a.date}</span>
                  <span>{a.department}</span>
                </p>
              </div>
            </div>

            {a.description && (
              <p style={{ margin: "10px 0", fontSize: 13, color: C.primary, lineHeight: 1.7 }}>
                {a.description}
              </p>
            )}

            {a.numbers && (
              <div style={{ padding: 10, background: `${C.teal}08`, borderRadius: 8, marginTop: 8, fontSize: 13, fontWeight: 700, color: C.teal }}>
                <TrendingUp size={14} /> {a.numbers}
              </div>
            )}

            {a.impact && (
              <p style={{ margin: "8px 0 0", padding: 8, background: `${C.success}08`, borderRadius: 6, fontSize: 12, color: C.success, fontWeight: 500 }}>
                <strong>الأثر:</strong> {a.impact}
              </p>
            )}
          </Card>
        ))}
      </div>

      {showAdd && (
        <Modal title="إضافة إنجاز جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_ACHIEVEMENT); }} width={620}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="عنوان الإنجاز" required placeholder="مثال: تخريج 500 مسلم جديد" value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <Field label="وصف الإنجاز" type="textarea" placeholder="تفاصيل الإنجاز..." rows={3} value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="التاريخ" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
              <Field label="القسم المسؤول" type="select" options={DEPARTMENT_OPTIONS} value={form.department} onChange={v => setForm({ ...form, department: v })} />
            </div>
            <Field label="الأرقام (مثلاً: 5000 مستفيد)" placeholder="5000 مستفيد" value={form.numbers} onChange={v => setForm({ ...form, numbers: v })} />
            <Field label="الأثر والنتائج" type="textarea" placeholder="ما الذي تحقق..." rows={2} value={form.impact} onChange={v => setForm({ ...form, impact: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Trophy} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_ACHIEVEMENT); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Demo Data
   ═════════════════════════════════════════════ */
const SOCIAL_PLATFORMS = [];
const RECENT_POSTS = [];
/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16 },
  sectionTitle: { display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 700, color: C.primary, margin: "0 0 14px" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  searchBox: { display: "flex", alignItems: "center", gap: 8, background: C.bg, borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320 },
  searchInput: { border: "none", background: "transparent", outline: "none", fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%" },
  filterSelect: { padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, background: C.white, cursor: "pointer" },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.6 },

  /* News */
  newsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 },
  newsCard: { padding: 0, overflow: "hidden", cursor: "pointer" },
  newsStrip: { height: 4, width: "100%" },
  newsMeta: { display: "flex", gap: 20, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.textLight },

  /* Social */
  platformsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 },
  platformCard: { padding: 20 },
  platformHeader: { display: "flex", alignItems: "center", gap: 12, marginBottom: 16 },
  platformIcon: { width: 48, height: 48, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center" },
  platformStats: { display: "flex", justifyContent: "space-around", padding: "14px 0", borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` },
  platformStat: { display: "flex", flexDirection: "column", alignItems: "center", gap: 2 },
  platformStatValue: { fontSize: 18, fontWeight: 700, color: C.primary },
  platformStatLabel: { fontSize: 11, color: C.textLight },
  platformGrowth: { display: "flex", alignItems: "center", gap: 6, marginTop: 12 },

  postItem: { display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 0", borderBottom: `1px solid ${C.bg}` },
  postPlatform: { padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" },

  /* Media */
  mediaGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 },
  mediaCard: { padding: 18 },
  mediaLink: { display: "inline-flex", alignItems: "center", gap: 4, marginTop: 10, fontSize: 12, color: C.teal, fontWeight: 600, textDecoration: "none" },
};
